import { modelData } from 'app/lib/modelData'
import { withZod } from '@remix-validated-form/with-zod'
import classNames from 'classnames'
import { clone, compact, entries, groupBy, isEmpty } from 'lodash'
import moment from 'moment'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { CurrentActivityIdContext, HighlightActivityContext } from '../activities/ActivityCard'
import { ActivityEmpty } from '../activities/ActivityEmpty'
import { ChangeCard } from '../activities/ChangeCard'
import { ClosedLostCard } from '../activities/ClosedLostCard'
import { ClosedWonCard } from '../activities/ClosedWonCard'
import { CustomActivityTypeCard } from '../activities/CustomActivityTypeCard'
import { FollowUpCard } from '../activities/FollowUpCard'
import { FormCard } from '../activities/FormCard'
import { ImportCard } from '../activities/ImportCard'
import { InboundCallCard } from '../activities/InboundCallCard'
import { JourneyMilestoneCard } from '../activities/JourneyMilestoneCard'
import { NoteCard } from '../activities/NoteCard'
import { OutboundCallCard } from '../activities/OutboundCallCard'
import { PatientCard } from '../activities/PatientCard'
import { SchedulerCard } from '../activities/SchedulerCard'
import { TaskCard } from '../activities/TaskCard'
import { ClosedLostFormData } from '../ClosedLostForm'
import { useConfirmation } from '../ConfirmationContext'
import { FeedDivider } from '../shared/styled/FeedDivider'
import { CommLogFeed, RecordFeed } from 'app/components/contacts/pms/RecordFeed'
import { LedgerRun } from 'app/components/contacts/pms/LedgerRun'
import type { RawPlan, TreatmentProcedure } from 'lib/treatmentCards'
import { UpdateRun } from 'app/components/contacts/updates/UpdateRun'
import { TreatmentWorkPanel } from 'app/components/contacts/treatment/TreatmentWorkPanel'
import {
  ACTIVITY_UPDATES,
  ActivityKind,
  DEFAULT_ACTIVITY_KINDS,
  filterActivitiesByKinds,
  kindsForActivity,
} from 'app/lib/activityKinds'
import { PMS_APPOINTMENTS, PMS_COMM_LOG, PMS_TRANSACTIONS, PmsRecordKind, RECORD_ACTIVITIES } from 'app/lib/pmsRecords'
import { Product } from 'app/components/auth/Product'
import { ActivitySections } from 'app/components/contacts/ContactRelatedObjects'
import { ContactInbox } from 'app/components/contacts/inbox/ContactInbox'
import type { ActivityContact, ActivityJson } from 'app/types/entities'
import type { AppointmentJson } from 'app/types/entities'
import type { Contact, ContactJson } from 'app/types/entities'
import type { MessageJson } from 'app/types/entities'
import type { TaskJson } from 'app/types/entities'
import type { TransactionJson } from 'app/types/entities'
import type { PlanWithLines } from 'app/types/treatment'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useActivityStore, useContactStore } from 'app/stores/StoreContext'
import { UPDATE_NOTE_MUTATION, UPDATE_MILESTONE_MUTATION } from 'app/stores/ActivityStore'
import {
  activityFeedDate,
  appointmentFeedDate,
  compareFeedDatesDesc,
  monthGroupLabel,
} from 'app/lib/activityFeedGrouping'
import { gqlMutation } from 'app/lib/graphqlClient'
import { dedupeOneWayMilestones, milestoneCardKind } from 'app/lib/journeyMilestones'
import { NOTE_MAX_LENGTH, NOTE_TOO_LONG_MESSAGE } from 'app/lib/noteMaxLength'
import { isTaskOverdue } from 'app/lib/taskDueDate'
import { useUser } from 'app/utils'
import { isSMCOrg } from 'lib/isSMCOrg'
import { logError, logWarn } from 'lib/logging'
import { Jsonify } from 'lib/toJSON'

const schema = z.object({
  type: z.string().min(1, { message: `Activity type required` }),
  contactId: z.string().min(1, { message: `Contact Id required` }),
})

export const closedLostNoteValidator = withZod(
  schema.extend({
    note: z.string().max(NOTE_MAX_LENGTH, { message: NOTE_TOO_LONG_MESSAGE }).optional(),
  }),
)

export default function ActivityPanel({
  type,
  contact,
  activities,
  messages,
  inboxContacts,
  appointments,
  transactions,
  treatmentPlans,
  onActivityAdded,
  onActivityUpdated,
  onActivityRemoved,
  onTaskUpdated,
  onMessage,
  onMarkAllRead,
  onMarkUnread,
  agentEnabled,
  aiHandling,
  hasActiveAgent,
  onAgentToggle,
  onContactRefresh,
  highlightActivityId,
  activityKinds,
  pmsKinds,
  commLog,
  treatmentWork,
  scrolled = false,
  onScrolledChange,
}: {
  type: ActivitySections
  contact?: ContactJson
  activities?: ActivityJson[]
  messages?: MessageJson[]
  inboxContacts?: Jsonify<ActivityContact>[]
  appointments?: AppointmentJson[]
  transactions?: TransactionJson[]
  treatmentPlans?: PlanWithLines[]
  onActivityAdded?: (activity: ActivityJson) => void
  onActivityUpdated?: (activity: ActivityJson) => void
  onActivityRemoved?: (activityId: string) => void
  onTaskUpdated?: (task: TaskJson) => void
  onMessage: (message: MessageJson) => void
  onMarkAllRead?: () => void | Promise<void>
  onMarkUnread?: () => void | Promise<void>
  agentEnabled?: boolean
  /** Agent owns the thread, so the Inbox tab shows the banner instead of a composer. */
  aiHandling?: boolean
  hasActiveAgent?: boolean
  onAgentToggle?: (val: boolean) => void | Promise<void>
  onContactRefresh?: () => void
  highlightActivityId?: string
  /** Which activity groups the chips have switched on. */
  activityKinds?: ActivityKind[]
  /** Which PMS record types the chips have switched on. */
  pmsKinds?: PmsRecordKind[]
  commLog?: CommLogFeed
  /** A contact's outstanding treatment, already classified by the drawer. */
  treatmentWork?: {
    procedures: TreatmentProcedure[]
    plans: RawPlan[]
    fetching: boolean
    hasError: boolean
    hasPatientLink: boolean
  }
  /**
   * Whether the feed has moved off its top. Owned by the parent because the
   * header it changes is a sibling of this panel, not a child.
   */
  scrolled?: boolean
  onScrolledChange?: (scrolled: boolean) => void
}): React.ReactElement {
  const user = useUser()
  const contactStore = useContactStore()
  const activityStore = useActivityStore()
  const [confirm] = useConfirmation()
  const [filteredActivities, setFilteredActivities] = useState<(ActivityJson | AppointmentJson | TransactionJson)[]>()
  const [currentAudioPlaying, setCurrentAudioPlaying] = useState<number | string>()
  const isPatientLinked = compact(contact?.patientIds).length > 0

  const groupedByDate: Record<
    string,
    (ActivityJson | AppointmentJson | MessageJson | TransactionJson | PlanWithLines)[]
  > = useMemo(() => {
    if (type === `all`) {
      // get over due tasks and sort them by due date
      const overDueTasks = (filteredActivities as ActivityJson[])
        ?.filter(
          (activity) =>
            activity.type === `Task` &&
            activity.data?.status === `open` &&
            // Calendar-day compare: a task due today is not overdue. See app/lib/taskDueDate.
            isTaskOverdue({ dueDate: activity.data?.dueDate }),
        )
        .sort((a, b) => {
          if (a.data?.dueDate && b.data?.dueDate) return moment(a.data.dueDate).diff(moment(b.data.dueDate))
          return 0
        })
      let sortedActivities = (filteredActivities as ActivityJson[])
        ?.filter(
          // remove over due tasks from sorted activities as they will be displayed above the other activities
          (activity) => overDueTasks.find((task) => task.id === activity.id) === undefined,
        )
        .sort((a, b) => compareFeedDatesDesc(activityFeedDate(a), activityFeedDate(b)))
      return {
        ...groupBy(sortedActivities, (activity: ActivityJson) => monthGroupLabel(activityFeedDate(activity))),
        overDueTasks: overDueTasks,
      }
    } else if (type === `inbox`) {
      return groupBy(messages, (messages: MessageJson) => monthGroupLabel(messages.createdDate))
    } else return {}
  }, [type, filteredActivities, messages, appointments, transactions, treatmentPlans])

  // The chips own the selection, so the feed derives from it rather than
  // keeping a third copy alongside the URL and the old selector's own state.
  useEffect(() => {
    // An absent selection means the standard set, never an empty feed.
    setFilteredActivities(
      dedupeOneWayMilestones(filterActivitiesByKinds(clone(activities) ?? [], activityKinds ?? DEFAULT_ACTIVITY_KINDS)),
    )
  }, [activities, activityKinds])

  if (!user) return null as unknown as React.ReactElement

  const handleEditNote = async (activityId, values) => {
    const result = await gqlMutation(UPDATE_NOTE_MUTATION, {
      id: activityId,
      input: { description: values.description },
    })
    if (result.error) {
      notifyError(`Error: ${result.error.message}`)
      logError(result.error.message)
    } else {
      const updated = modelData(result.data?.updateNote, ['data'])
      if (updated) {
        notifySuccess(`Note edited successfully`)
        setFilteredActivities((prev) => prev?.map((activity) => (activity.id === activityId ? updated : activity)))
      }
    }
  }

  const handleUpdateClosedLost = async (activityId: string, data: ClosedLostFormData, onSuccess: () => void) => {
    if (contact) {
      const contactUpdate = {
        leadStatus: `Closed Lost`,
        closedLostReason: data.closedLostReason,
        isQualified: data.disqualified === `true` ? false : true,
      }
      const updateResult = await contactStore.update(contact.id!, contactUpdate)
      if (updateResult.error) {
        logError(`Error updating contact`, updateResult.error.message)
      } else {
        Object.assign(contact, contactUpdate)
      }
    }
    const closedLostData = {
      isQualified: isSMCOrg(user.orgId)
        ? ![`Service Not Offered`, `Insurance: State / HMO`, `Office Too Far`].includes(data.closedLostReason)
        : data.disqualified === `false`,
      closedLostReason: data.closedLostReason,
      ownerId: data.owner?.id,
      ownerName: data.owner?.name,
      note: data.note,
    }

    const actResult = await gqlMutation(UPDATE_MILESTONE_MUTATION, {
      id: activityId,
      input: closedLostData,
    })
    if (actResult.error) {
      notifyError(`Error: ${actResult.error.message}`)
      logWarn(`Error updating Closed Lost activity`, actResult.error.message)
      return
    } else {
      const updated = modelData(actResult.data?.updateMilestone, ['data'])
      notifySuccess(`Closed Lost updated successfully`)
      onSuccess()
      if (updated) {
        setFilteredActivities((prev) => prev?.map((activity) => (activity.id === activityId ? updated : activity)))
        // GraphQL JSON does not describe the activity data structure.
        onActivityUpdated?.({ ...updated, data: updated.data as ActivityJson['data'] })
      }
    }
  }
  const handleDelete = async (activityId) => {
    await confirm({
      title: `Delete Note`,
      description: `Are you sure you want to delete this note?`,
      variant: `danger`,
    }).then(async () => {
      const result = await activityStore.remove(activityId)
      if (result.error) {
        notifyError(`Error: ${result.error.message}`)
        logError(result.error.message)
      } else {
        onActivityRemoved?.(activityId)
        notifySuccess(`Note deleted`)
      }
    })
  }

  let getActivityCard = (activity: ActivityJson, isFirst: boolean, isLast: boolean) => {
    switch (activity.type) {
      case `Milestone`: {
        const kind = milestoneCardKind(activity.description)
        if (kind === `closed-won`) {
          return <ClosedWonCard activity={activity} contact={contact} isFirst={isFirst} isLast={isLast} alwaysOpen />
        }
        if (kind === `journey`) {
          return <JourneyMilestoneCard activity={activity} isFirst={isFirst} isLast={isLast} alwaysOpen />
        }
        return (
          <ClosedLostCard
            activity={activity}
            contact={contact}
            isFirst={isFirst}
            isLast={isLast}
            handleEdit={handleUpdateClosedLost}
            alwaysOpen
          />
        )
      }
      case `FollowUp`:
        return (
          <FollowUpCard
            activity={activity}
            contact={contact}
            isFirst={isFirst}
            isLast={isLast}
            handleEdit={(activity) => onActivityAdded?.(activity)}
            handleDelete={() => onActivityRemoved?.(activity.id!)}
            onActivityUpdated={onActivityUpdated}
            alwaysOpen
          />
        )
      case `Note`:
      case `Medical Note`:
        return (
          <NoteCard
            activity={activity}
            handleEdit={handleEditNote}
            handleDelete={handleDelete}
            isFirst={isFirst}
            isLast={isLast}
          />
        )
      case `Change`:
        return <ChangeCard activity={activity} isFirst={isFirst} isLast={isLast} alwaysOpen />
      case `Patient`:
        return <PatientCard activity={activity} isFirst={isFirst} isLast={isLast} />
      case `Form`:
        return <FormCard activity={activity} isFirst={isFirst} isLast={isLast} alwaysOpen />
      case `Call`:
        return activity.subType === `outbound` ? (
          <OutboundCallCard
            activity={activity}
            contact={contact}
            currentAudioPlaying={currentAudioPlaying}
            onAudioPlay={() => setCurrentAudioPlaying(activity.id)}
            onActivityUpdated={(activity) => activity && onActivityUpdated?.(activity)}
            onContactRefresh={onContactRefresh}
            isFirst={isFirst}
            isLast={isLast}
          />
        ) : (
          <InboundCallCard
            activity={activity}
            contact={contact}
            currentAudioPlaying={currentAudioPlaying}
            onAudioPlay={() => setCurrentAudioPlaying(activity.id)}
            onActivityUpdated={(activity) => activity && onActivityUpdated?.(activity)}
            onContactRefresh={onContactRefresh}
            isFirst={isFirst}
            isLast={isLast}
            alwaysOpen
          />
        )
      case `Chat`:
      case `Imported Form`:
      case `Virtual Consultation`:
      case `Online Scheduler`:
        return (
          <ImportCard
            activity={activity}
            isFirst={isFirst}
            isLast={isLast}
            alwaysOpen={activity.type === `Online Scheduler`}
          />
        )
      case `Scheduler`:
        return <SchedulerCard activity={activity} isFirst={isFirst} isLast={isLast} alwaysOpen />
      case `Task`:
        return (
          <TaskCard
            activity={activity}
            contact={contact}
            handleDelete={() => onActivityRemoved?.(activity.id!)}
            onTaskUpdated={onTaskUpdated}
            isFirst={isFirst}
            isLast={isLast}
          />
        )

      default:
        return <CustomActivityTypeCard activity={activity} isFirst={isFirst} isLast={isLast} alwaysOpen />
    }
  }

  return (
    <HighlightActivityContext.Provider value={highlightActivityId}>
      <div className="relative flex h-full flex-col overflow-hidden">
        {/* Header and feed are both white, so only a shadow says content went under it. */}
        <div
          aria-hidden
          className={classNames(
            `pointer-events-none absolute inset-x-0 top-0 z-10 h-3 bg-gradient-to-b from-trn-900/[0.07] to-transparent transition-opacity duration-200`,
            scrolled ? `opacity-100` : `opacity-0`,
          )}
        />
        {/*
          pt-4 so the first date divider clears the filter row above it. The
          gap only spaced siblings, leaving the first one against the header
          border. Padding rather than a margin on the divider so it belongs to
          the scroll region and every feed type gets it.
        */}
        <div
          className="flex grow flex-col gap-y-3 overflow-y-auto pt-4"
          onScroll={(event) => onScrolledChange?.(event.currentTarget.scrollTop > 0)}
        >
          {type === `all` && (
            <RecordFeed
              kinds={pmsKinds ?? [RECORD_ACTIVITIES, PMS_APPOINTMENTS]}
              activities={(filteredActivities ?? []).map((activity) => ({
                id: (activity as ActivityJson).id,
                date: activityFeedDate(activity as ActivityJson),
                item: activity,
              }))}
              renderActivity={(item, isFirst, isLast) => getActivityCard(item as ActivityJson, isFirst, isLast)}
              appointments={appointments}
              commLog={commLog}
              // Updates are bookkeeping and arrive scattered through a month,
              // so they gather into one card per month rather than folding only
              // where they happen to be neighbours.
              /*
               * Activities are the one kind the feed carries as `unknown`: the
               * panel owns their cards, so the feed never looks inside one.
               * That makes this the only place the shape has to be asserted.
               */
              // A milestone rides the Updates chip but keeps its own card: it
              // carries a reason and a note, and the fold would cut it down to
              // one line.
              foldKey={(record) =>
                record.kind === RECORD_ACTIVITIES &&
                (record.item as ActivityJson).type !== `Milestone` &&
                kindsForActivity(record.item as ActivityJson).includes(ACTIVITY_UPDATES)
                  ? ACTIVITY_UPDATES
                  : undefined
              }
              renderFold={(records) => <UpdateRun activities={records.map((r) => r.item as ActivityJson)} />}
            />
          )}
          {/*
            RecordFeed does its own grouping and its own empty state, so this
            legacy one must stay out of its way or both render at once.
          */}
          {isEmpty(groupedByDate) && type === `inbox` && (
            // <div className="mt-40 flex h-full grow px-32 pt-2">
            <div className="flex h-full grow items-center justify-center px-32 pt-2">
              <ActivityEmpty type={type} isPatientLinked={isPatientLinked} />
            </div>
          )}
          {type === `inbox` && (
            <Product type="Inbox">
              <ContactInbox
                conversationKey={contact?.id ?? contact?.cellPhone ?? `activity-panel-inbox`}
                messages={groupedByDate as Record<string, MessageJson[]>}
                relatedContacts={inboxContacts}
                contact={contact}
                showInlineMarkRead
                showInlineAgentToggle={hasActiveAgent === true}
                showAgentToolCalls
                agentEnabled={agentEnabled}
                aiHandling={aiHandling}
                onAgentToggle={onAgentToggle}
                onTakeOver={onAgentToggle && (() => onAgentToggle(false))}
                onMarkAllRead={onMarkAllRead}
                onMarkUnread={onMarkUnread}
                onMessage={onMessage}
              />
            </Product>
          )}
          {type === `ledger` && (
            <RecordFeed
              kinds={[`transactions`]}
              transactions={transactions}
              groupBy="day"
              collapseKey={(record) =>
                record.kind === PMS_TRANSACTIONS ? (record.item.transactionType ?? undefined) : undefined
              }
              renderRun={(records) => (
                <LedgerRun transactions={records.flatMap((r) => (r.kind === PMS_TRANSACTIONS ? [r.item] : []))} />
              )}
            />
          )}
          {type === `treatment-plans` && (
            <TreatmentWorkPanel
              procedures={treatmentWork?.procedures ?? []}
              plans={treatmentWork?.plans ?? []}
              fetching={!!treatmentWork?.fetching}
              hasError={!!treatmentWork?.hasError}
              hasPatientLink={!!treatmentWork?.hasPatientLink}
            />
          )}
        </div>
      </div>
    </HighlightActivityContext.Provider>
  )
}
